import { useState, useEffect } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import { FaSync, FaUser, FaFileMedical, FaSearch, FaFlask, FaDesktop, FaTimes, FaPlus, FaUserMd, FaCheck } from "react-icons/fa";
import PageHeading from "../../components/PageHeading";
import LabEditor from "./LabEditor";
import DynamicSelect from "../../components/DynamicSelect";
import AddPatientModal from "./AddPatientModal";

const API_KEY = "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";

// API URLs
const TEMPLATE_API = "https://api.muktihospital.com/api/pathology/templates";
const REPORT_API = "https://api.muktihospital.com/api/pathology/reports/add";
const PATIENT_API = "https://api.muktihospital.com/api/patient";
const DOCTOR_API = "https://api.muktihospital.com/api/doctor";

const SyncConfirmModal = ({ isOpen, onClose, onConfirm, isSyncing }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden transform transition-all scale-100">
        
        {/* Header */}
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-100 flex justify-between items-center">
          <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
            <FaDesktop className="text-blue-600" /> PC Sync
          </h3>
          <button onClick={onClose} disabled={isSyncing} className="text-gray-400 hover:text-red-500 transition-colors">
            <FaTimes />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 text-center">
          <div className="w-16 h-16 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
             <FaSync className={`text-2xl ${isSyncing ? 'animate-spin' : ''}`} />
          </div>
          <h4 className="text-gray-800 font-semibold text-lg mb-2">Sync Templates?</h4>
          <p className="text-gray-500 text-sm mb-4">
            This will scan <span className="font-mono bg-gray-100 px-1 rounded text-gray-700">D:\Hospital\Templates</span> for new <b>.docx</b> files and import them into the system.
          </p>
        </div>

        {/* Footer Actions */}
        <div className="px-6 py-4 bg-gray-50 flex gap-3 justify-end">
          <button 
            onClick={onClose} 
            disabled={isSyncing}
            className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
          >
            Cancel
          </button>
          <button 
            onClick={onConfirm} 
            disabled={isSyncing}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {isSyncing ? (
                <>Processing...</>
            ) : (
                <>Yes, Sync Now</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const NewLabReport = () => {
  const [templates, setTemplates] = useState([]);
  const [allPatients, setAllPatients] = useState([]);
  const [patients, setPatients] = useState([]);

  // Selection States
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedTemplateOption, setSelectedTemplateOption] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);

  const [allDoctors, setAllDoctors] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [doctorSearchQuery, setDoctorSearchQuery] = useState("");

  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  
  const [syncing, setSyncing] = useState(false);
  const [isFullySynced, setIsFullySynced] = useState(false);
  
  const [isSyncModalOpen, setIsSyncModalOpen] = useState(false);
  const [isAddPatientOpen, setIsAddPatientOpen] = useState(false);

  const breadcrumbs = [
    { label: "Dashboard", url: "/" },
    { label: "Pathology", url: "/pathology" },
    { label: "New Lab Report" },
  ];

  // --- DATA FETCHING ---
  const fetchTemplates = async () => {
    try {
      const res = await axios.get(TEMPLATE_API, { headers: { "x-api-key": API_KEY } });
      if (Array.isArray(res.data)) setTemplates(res.data);
      else if (res.data.templates && Array.isArray(res.data.templates)) setTemplates(res.data.templates);
      else setTemplates([]);
    } catch (err) {
      console.error("Error loading templates:", err);
      toast.error("Failed to load templates");
    }
  };

  const fetchPatients = async () => {
    try {
      const response = await axios.get(PATIENT_API, { headers: { "x-api-key": API_KEY } });
      let patientData = [];
      if (Array.isArray(response.data)) patientData = response.data;
      else if (response.data.patients) patientData = response.data.patients;
      else if (response.data.data) patientData = response.data.data;
      setAllPatients(patientData);
    } catch (error) {
      console.error("Error fetching patients:", error);
      toast.error("Failed to load patient list");
    }
  };

  const fetchDoctors = async () => {
    try {
      const response = await axios.get(DOCTOR_API, { headers: { "x-api-key": API_KEY } });
      const doctorsData = Array.isArray(response.data) ? response.data : (response.data.data || []);
      setAllDoctors(doctorsData);
    } catch (error) { console.error("Error fetching doctors:", error); }
  };

  const checkSyncStatus = async () => {
    try {
      const res = await axios.get(`${TEMPLATE_API}/sync-status`, { headers: { "x-api-key": API_KEY } });
      setIsFullySynced(res.data.isSynced);
    } catch (err) {
      console.error("Status check failed", err);
    }
  };

  useEffect(() => {
    fetchTemplates();
    checkSyncStatus();
    fetchPatients();
    fetchDoctors();
  }, []);

  const handleDoctorSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setDoctorSearchQuery(query);
    if (query.length > 0) {
      const filtered = allDoctors.filter(d => (d.translations?.en?.name?.toLowerCase() || "").includes(query));
      setDoctors(filtered.slice(0, 10));
    } else { setDoctors([]); }
  };

  const handleDoctorSelect = (doc) => {
    setSelectedDoctor(doc);
    setDoctors([]);
    setDoctorSearchQuery("");
  };

  // --- HANDLERS ---
const handleSyncClick = () => {
    setIsSyncModalOpen(true);
  };

  const executeSync = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${TEMPLATE_API}/import-from-pc`, {}, { headers: { "x-api-key": API_KEY } });
      toast.success(`Sync Complete! ${res.data.count || 'Templates'} updated.`);
      await fetchTemplates();
      await checkSyncStatus();
      setIsSyncModalOpen(false);
    } catch (err) {
      toast.error("Sync failed.");
    } finally {
      setSyncing(false);
    }
  };
  const handlePatientSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);
    if (query.length > 0) {
      const filtered = allPatients.filter((p) => {
        const name = p.name ? p.name.toLowerCase() : "";
        const id = p.id ? String(p.id).toLowerCase() : "";
        const phone = p.phoneNumber ? String(p.phoneNumber) : "";
        return name.includes(query) || id.includes(query) || phone.includes(query);
      });
      setPatients(filtered.slice(0, 10));
    } else {
      setPatients([]);
    }
  };

  const handlePatientSelect = (p) => {
    setSelectedPatient(p);
    setPatients([]);
    setSearchQuery("");
  };

  const handleNewPatientAdded = (newPatient) => {
      setAllPatients(prev => [newPatient, ...prev]);

      setSelectedPatient(newPatient);
      
      setPatients([]);
      setSearchQuery("");
  };

  const handleTemplateChange = async (selectedOption) => {
    setSelectedTemplateOption(selectedOption);
    if (!selectedOption) {
      setSelectedTemplate(null);
      return;
    }

    setLoading(true);
    try {
      const res = await axios.get(`${TEMPLATE_API}/${selectedOption.value}`, {
        headers: { "x-api-key": API_KEY },
      });
      if (res.data && res.data.content) setSelectedTemplate(res.data);
      else if (res.data.template) setSelectedTemplate(res.data.template);
    } catch (err) {
      toast.error("Failed to load template design");
    } finally {
      setLoading(false);
    }
  };

const handleSaveReport = async (content) => {
    if (!selectedPatient || !selectedTemplate) {
        toast.warning("Please select patient and test first!");
        return;
    }

    try {
        let finalReferredBy = "";

        if (selectedDoctor && selectedDoctor.translations?.en?.name) {
            finalReferredBy = selectedDoctor.translations.en.name;
        } else if (selectedPatient.referredBy) {
            finalReferredBy = selectedPatient.referredBy;
        } else {
            finalReferredBy = "Self"; 
        }

        await axios.post(REPORT_API, {
            patientId: String(selectedPatient.id),
            templateId: selectedTemplate.id,
            reportContent: content,
            referredBy: finalReferredBy,
        }, { headers: { "x-api-key": API_KEY } });

        toast.success("Report saved successfully!");
    } catch (err) {
        console.error(err);
        toast.error("Failed to save report.");
    }
};

  const templateOptions = templates.map(t => ({
    value: t.id,
    label: t.name
  }));

  return (
    <div className="bg-gray-50 pb-10">
      <ToastContainer position="top-right" className="print:hidden" />
      <SyncConfirmModal 
        isOpen={isSyncModalOpen} 
        onClose={() => setIsSyncModalOpen(false)} 
        onConfirm={executeSync} 
        isSyncing={syncing}
      />
      <AddPatientModal
        isOpen={isAddPatientOpen}
        onClose={() => setIsAddPatientOpen(false)}
        onSuccess={handleNewPatientAdded}
      />

      <div className="print:hidden pt-6">
        <PageHeading title="New Lab Report" breadcrumbs={breadcrumbs} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mt-6">

        {/* LEFT SIDE: CONTROLS */}
        <div className="lg:col-span-4 print:hidden space-y-6">

          {/* 1. PATIENT SELECTION CARD */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="px-5 py-4 border-b border-gray-100 flex justify-between items-center gap-3">
                <div className="flex items-center gap-3">
                   <div className="bg-blue-100 p-2 rounded-full text-blue-600"><FaUser size={14}/></div>
                   <h3 className="font-bold text-gray-800 text-base">Select Patient</h3>
                </div>
                <button 
                    onClick={() => setIsAddPatientOpen(true)}
                    className="p-2 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-full transition-colors"
                    title="Add New Patient"
                >
                    <FaPlus size={12} />
              </button>
            </div>
            <div className="p-5">
                {selectedPatient ? (
                    <div className="bg-green-50 border border-green-100 rounded-lg p-4 relative group">
                        <button
                            onClick={() => setSelectedPatient(null)}
                            className="absolute top-2 right-2 text-red-500 hover:text-red-700 text-sm font-bold bg-white px-2 py-1 rounded shadow-sm opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                            Change
                        </button>
                        <h4 className="font-bold text-gray-900 text-base">{selectedPatient.name}</h4>
                        <div className="text-sm text-gray-600 mt-2 space-y-1">
                            <p>ID: <span className="font-mono text-gray-800 font-bold">{selectedPatient.id}</span></p>
                            <p>Age: {selectedPatient.age || 'N/A'}</p>
                            <p>Ph: {selectedPatient.phoneNumber}</p>
                        </div>
                    </div>
                ) : (
                    <div className="relative">
                        <div className="relative">
                            <input
                                type="text"
                                placeholder="Search Name / ID..."
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-base focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all shadow-sm"
                                value={searchQuery}
                                onChange={handlePatientSearch}
                            />
                            <FaSearch className="absolute left-3.5 top-4 text-gray-400 text-sm" />
                        </div>

                        {/* Dropdown Results */}
                        {patients.length > 0 && (
                            <ul className="absolute z-50 w-full bg-white border border-gray-200 mt-1 rounded-lg shadow-xl max-h-72 overflow-y-auto">
                                {patients.map((p) => (
                                    <li key={p.id} onClick={() => handlePatientSelect(p)}
                                        className="px-4 py-3 border-b border-gray-50 hover:bg-blue-50 cursor-pointer transition-colors"
                                    >
                                        <div className="text-sm font-bold text-gray-800">{p.name}</div>
                                        <div className="text-sm text-gray-500 flex justify-between mt-1">
                                            <span>{p.phoneNumber}</span>
                                            <span className="bg-gray-100 px-2 py-0.5 rounded text-xs font-mono font-semibold">ID: {p.id}</span>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3">
                <div className="bg-green-100 p-2 rounded-full text-green-600"><FaUserMd size={14}/></div>
                <h3 className="font-bold text-gray-800 text-base">Referred By (Doctor)</h3>
            </div>
            <div className="p-5">
                {selectedDoctor ? (
                    <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 relative group">
                        <button onClick={() => setSelectedDoctor(null)} className="absolute top-2 right-2 text-red-500 hover:text-red-700 text-sm font-bold bg-white px-2 py-1 rounded shadow-sm opacity-0 group-hover:opacity-100 transition-opacity">Change</button>
                        <h4 className="font-bold text-gray-900 text-base">{selectedDoctor.translations?.en?.name || "Unknown Doctor"}</h4>
                        <p className="text-sm text-gray-600 mt-1">{selectedDoctor.translations?.en?.designation || "Doctor"}</p>
                    </div>
                ) : (
                    <div className="relative">
                        <div className="relative">
                            <input type="text" placeholder="Search Doctor..." className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-base focus:ring-2 focus:ring-green-500 outline-none transition-all shadow-sm" value={doctorSearchQuery} onChange={handleDoctorSearch} />
                            <FaSearch className="absolute left-3.5 top-4 text-gray-400 text-sm" />
                        </div>
                        {doctors.length > 0 && (
                            <ul className="absolute z-50 w-full bg-white border border-gray-200 mt-1 rounded-lg shadow-xl max-h-72 overflow-y-auto">
                                {doctors.map((d) => (
                                    <li key={d.id} onClick={() => handleDoctorSelect(d)} className="px-4 py-3 border-b border-gray-50 hover:bg-green-50 cursor-pointer transition-colors">
                                        <div className="text-sm font-bold text-gray-800">{d.translations?.en?.name}</div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}
            </div>
          </div>
          {/* 2. TEST SELECTION CARD */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="bg-purple-100 p-2 rounded-full text-purple-600"><FaFileMedical size={14}/></div>
                    <h3 className="font-bold text-gray-800 text-base">Select Test</h3>
                </div>

                {/* Sync Button */}
                <button
                    onClick={handleSyncClick}
                    disabled={syncing || isFullySynced}
                    className={`p-2 rounded-full transition-all ${
                        syncing ? "bg-yellow-100 text-yellow-600 animate-spin" :
                        isFullySynced ? "bg-green-100 text-green-600 cursor-default" : "bg-blue-100 text-blue-600 hover:bg-blue-200"
                    }`}
                    title="Sync Templates from PC"
                >
                    <FaSync size={12} />
                </button>
            </div>

            <div className="p-5">
                <DynamicSelect
                    options={templateOptions}
                    value={selectedTemplateOption}
                    onChange={handleTemplateChange}
                    isSearchable={true}
                    placeholder="Search Test Template..."
                    controlBorderColor="#d1d5db"
                    controlFontSize="16px"
                    controlBackgroundColor="white"
                />

                <div className="mt-4 flex items-start gap-2 bg-blue-50 p-3 rounded-md text-xs text-blue-700 leading-relaxed">
                    <span className="mt-0.5 text-base">💡</span>
                    <p>Select a template to load the editor. Ensure PC Sync is active if templates are missing.</p>
                </div>
            </div>
          </div>

        </div>

        {/* RIGHT SIDE: EDITOR / EMPTY STATE */}
        <div className="lg:col-span-8">

            {loading ? (
                <div className="flex flex-col items-center justify-center bg-white rounded-xl shadow-sm h-[600px]"><div className="animate-spin rounded-full h-12 w-12 border-b-4 border-blue-600 mb-6"></div><p className="text-gray-500 font-medium text-lg">Loading Template...</p></div>
            ) : selectedTemplate && selectedPatient && selectedDoctor ? (
                <LabEditor
                    key={`${selectedTemplate.id}-${selectedPatient.id}`}
                    template={selectedTemplate}
                    patient={selectedPatient}
                    doctor={selectedDoctor}
                    onSave={handleSaveReport}
                />
            ) : (
                <div className="h-full min-h-[600px] flex flex-col items-center justify-center bg-white rounded-xl shadow-sm border border-gray-200 p-10 text-center relative overflow-hidden">
                     {/* Decorative background */}
                     <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-blue-50 rounded-full blur-3xl opacity-50 -z-10"></div>

                     <div className="bg-white p-6 rounded-full shadow-sm mb-6 relative z-10">
                        <FaFlask className="text-blue-500 text-5xl" />
                     </div>
                     
                     <h2 className="text-2xl font-bold text-gray-800 mb-3">Start a New Report</h2>
                     <p className="text-gray-500 max-w-md mb-8 leading-relaxed">
                        To generate a report, please complete the selection process on the left panel.
                     </p>

                     {/* Steps Indicator */}
                     <div className="flex flex-col sm:flex-row items-center gap-4 text-sm font-medium w-full justify-center">
                        
                        {/* Step 1: Patient */}
                        <div className={`flex items-center gap-2 ${selectedPatient ? "text-green-600" : "text-gray-400"}`}>
                            <span className={`w-8 h-8 flex items-center justify-center rounded-full border-2 ${selectedPatient ? "border-green-600 bg-green-50" : "border-gray-300 bg-white"}`}>
                                {selectedPatient ? <FaCheck size={12} /> : "1"}
                            </span>
                            <span>Select Patient</span>
                        </div>

                        <div className={`hidden sm:block w-12 h-0.5 ${selectedPatient ? "bg-green-600" : "bg-gray-200"}`}></div>

                        {/* Step 2: Doctor */}
                        <div className={`flex items-center gap-2 ${selectedDoctor ? "text-green-600" : "text-gray-400"}`}>
                            <span className={`w-8 h-8 flex items-center justify-center rounded-full border-2 ${selectedDoctor ? "border-green-600 bg-green-50" : "border-gray-300 bg-white"}`}>
                                 {selectedDoctor ? <FaCheck size={12} /> : "2"}
                            </span>
                            <span>Select Doctor</span>
                        </div>

                        <div className={`hidden sm:block w-12 h-0.5 ${selectedDoctor ? "bg-green-600" : "bg-gray-200"}`}></div>

                        {/* Step 3: Test */}
                        <div className={`flex items-center gap-2 ${selectedTemplate ? "text-green-600" : "text-gray-400"}`}>
                            <span className={`w-8 h-8 flex items-center justify-center rounded-full border-2 ${selectedTemplate ? "border-green-600 bg-green-50" : "border-gray-300 bg-white"}`}>
                                 {selectedTemplate ? <FaCheck size={12} /> : "3"}
                            </span>
                            <span>Select Test</span>
                        </div>
                     </div>
                </div>
            )}
        </div>

      </div>
    </div>
  );
};

export default NewLabReport;